import { KioskSdk } from '@b2bsoft/kiosk-common-sdk';
import { traceContext, TraceContext } from '@b2bsoft/tracecontext';
import { observable, action, makeObservable } from 'mobx';

import LoaderModel from './shared/LoaderModel';
import { http, setupAPI } from 'kioskApp/api';
import { Timeouts } from 'shared/helpers';
import ConfigurationModel from './shared/ConfigurationModel';
import WizardModel from './kiosk/WizardModel';
import { Billpay } from './kiosk/Billpay';

export class StoreModel {
    public kioskSdk: KioskSdk;

    @observable public loader: LoaderModel;

    @observable public configuration: ConfigurationModel;

    @observable public traceContext: TraceContext;

    @observable billpay: Billpay;
    @observable wizard: WizardModel;

    @observable public brand: string | null = null;
    @observable public kioskId: string | null = null;
    @observable public accessCode: string | null = null;
    @observable public appId: string | null = null;
    @observable public sku: string | null = null;
    @observable public description: string | null = null;
    @observable public serviceCode: string | null = null;
    @observable public paymentApiUrl: string | null = null;

    @observable public isReady: boolean = false;

    @observable public language: string | null = null;

    /**
     * It's a feature that will allow our QA to manually input response code for all API requests
     * This is needed for better QA experience, to fairly test all cases/responses from API
     * @type {boolean}
     * @memberof StoreModel
     */
    public isQA: boolean = false;

    constructor() {
        this.loader = new LoaderModel();
        this.configuration = new ConfigurationModel();

        this.billpay = new Billpay(this);

        this.wizard = new WizardModel(this);

        makeObservable(this);
    }

    @action
    setValue(data: any) {
        Object.assign(this, data);
    }

    async init() {
        this.traceContext = traceContext;

        this.kioskSdk = new KioskSdk({
            timeout: Timeouts.SdkGlobal,
            // logging: true
        });

        this.kioskSdk.register();

        await this.login();
        await this.configuration.init();
        this.wizard && this.wizard.init();
    }

    @action setReady() {
        this.isReady = true;
    }

    async login() {
        const { data: sessionData } = await this.kioskSdk.getSession();

        this.traceContext.startFrom(sessionData.traceparent);

        this.setValue({
            accessCode: sessionData.accessCode,
            brand: sessionData.brand,
            kioskId: sessionData.kioskId,
            language: sessionData.language,
            isQA: sessionData.isQa,
        });

        setupAPI(this, {
            isQA: this.isQA,
            accessCode: this.accessCode,
            brand: this.brand,
            kioskId: this.kioskId,
            locationId: sessionData.locationId,
            token: sessionData.token,
        });

        await http.get('/api/account/login').then((response: any) => {
            this.setValue({
                appId: response.data.appId,
                sku: response.data.sku,
                description: response.data.description,
                serviceCode: response.data.serviceCode,
                paymentApiUrl: response.data.amdocsApi,
            });
        });
    }
}

export default StoreModel;

export const Store = new StoreModel();
